import { useState, useEffect } from "react";
import { Lock, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

type FieldConfig = {
  fieldname: string;
  label: string;
  required?: boolean;
};

type ListAvailableColumnsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: FieldConfig[];
  // Currently selected visible fields
  selectedFieldnames: string[];
  // Called when user clicks Save in this modal
  onSave: (nextVisibleFieldnames: string[]) => void;
};

export function ListAvailableColumnsModal({
  open,
  onOpenChange,
  fields,
  selectedFieldnames,
  onSave,
}: ListAvailableColumnsModalProps) {
  // Local state only - changes are not persisted until Save
  const [localSelection, setLocalSelection] = useState<string[]>(selectedFieldnames);

  // Sync local state when dialog opens
  useEffect(() => {
    if (open) {
      setLocalSelection(selectedFieldnames);
    }
  }, [open, selectedFieldnames]);

  const handleSave = () => {
    // Update parent's localOrder, but don't persist to store yet
    onSave(localSelection);
    onOpenChange(false); // Close only this modal, main stays open
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 flex flex-col overflow-hidden">
        {/* Checkmark button - top right */}
        <Button
          type="button"
          onClick={handleSave}
          className={cn(
            "absolute right-4 top-4 z-50 rounded-full bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-apple-sm",
            "focus:outline-none focus:ring-0",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          size="icon"
        >
          <Check className="w-5 h-5 text-white" />
          <span className="sr-only">Save</span>
        </Button>

        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto relative">
            <DialogHeader className="px-10 pt-20 pb-4">
              <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px]">
                Available Columns
              </DialogTitle>
            </DialogHeader>

            <div className="pb-8 px-10 pt-2">
              <div className="py-4 max-h-[60vh] overflow-y-auto">
          <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
            {fields.map((field) => {
              const isVisible = localSelection.includes(field.fieldname);
              return (
                <div
                  key={field.fieldname}
                  className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0"
                >
                  <div className="flex items-center gap-2">
                    {field.required && (
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    )}
                    <Label 
                      htmlFor={field.fieldname}
                      className="text-[17px] text-gray-900 dark:text-gray-100 font-normal"
                    >
                      {field.label}
                    </Label>
                  </div>
                  <Switch
                    id={field.fieldname}
                    checked={isVisible}
                    onCheckedChange={(checked) => {
                      if (field.required) return;
                      if (checked) {
                        setLocalSelection([...localSelection, field.fieldname]);
                      } else {
                        setLocalSelection(localSelection.filter(f => f !== field.fieldname));
                      }
                    }}
                    disabled={field.required}
                  />
                </div>
              );
            })}
              </div>
              </div>
            </div>
            
            {/* Bottom fade-out gradient */}
            <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
          </div>
          
          {/* Sticky Save Button */}
          <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
            <Button
              type="button"
              onClick={handleSave}
              className="w-full text-[17px] font-semibold bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white px-7 py-3.5 h-auto rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

